import React, { useState, useEffect } from 'react';
import fetchClient from 'interfaces/fetchClient';


interface CustomField {
	label: string;
	key: string;
	type: 'text' | 'number' | 'date' | 'boolean' | 'textarea';
	required: boolean;
	defaultValue?: any;
}

interface ProductionConfigResponse {
	_id: string;
	ownerId: string;
	nbSteps: 1 | 2 | 3;
	customStepName?: string;
	customFields: CustomField[];
}

interface DynamicProductionFormProps {
	productionId: string;
	onSuccess?: () => void;
	onCancel?: () => void;
}

/**
 * Component React for managing custom production steps
 * Dynamically generates fields based on user configuration
 */
const DynamicProductionForm: React.FC<DynamicProductionFormProps> = ({
	productionId,
	onSuccess,
	onCancel,
}) => {
	const [config, setConfig] = useState<ProductionConfigResponse | null>(null);
	const [customData, setCustomData] = useState<Record<string, any>>({});
	const [errors, setErrors] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingConfig, setLoadingConfig] = useState(true);

	// Load configuration on component mount
	useEffect(() => {
		fetchConfig();
	}, []);

	const fetchConfig = async () => {
		try {
			const response = await fetchClient.get('/productions/config/workflow', {
				headers: {
					'Authorization': 'Bearer ' + localStorage.getItem('token'),
				},
			});

			const data = response.data;
			setConfig(data);

			// Initialize default values for custom fields
			const defaultValues: Record<string, any> = {};
			data.customFields.forEach((field: CustomField) => {
				if (field.defaultValue !== undefined) {
					defaultValues[field.key] = field.defaultValue;
				}
			});
			setCustomData(defaultValues);
		} catch (error) {
			console.error('Error fetching config:', error);
			setErrors(['Impossible de charger la configuration']);
		} finally {
			setLoadingConfig(false);
		}
	};

	const handleInputChange = (key: string, value: any) => {
		setCustomData((prev) => ({
			...prev,
			[key]: value,
		}));
		// Clear errors on input change
		setErrors([]);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setErrors([]);

		try {
			const response = await fetchClient.post(
				`/productions/advance/${productionId}`,
				{ customData },
				{
					headers: {
						'Authorization': 'Bearer ' + localStorage.getItem('token'),
					},
				}
			);

			const result = response.data;

			if (result.errors) {
				if (result.errors) {
					setErrors(result.errors);
				} else {
					setErrors([result.message || 'Une erreur est survenue']);
				}
				return;
			}

			// Succès
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			console.error('Error advancing production:', error);
			setErrors(['Erreur de connexion au serveur']);
		} finally {
			setLoading(false);
		}
	};

	const renderField = (field: CustomField) => {
		const value = customData[field.key] ?? '';

		const baseClasses = `
			w-full px-4 py-2 rounded-lg border border-gray-300
			focus:border-blue-500 focus:ring-2 focus:ring-blue-200
			transition-colors duration-200
		`;

		switch (field.type) {
			case 'text':
				return (
					<input
						type="text"
						value={value}
						onChange={(e) => handleInputChange(field.key, e.target.value)}
						className={baseClasses}
						required={field.required}
						placeholder={`Entrez ${field.label.toLowerCase()}`}
					/>
				);

			case 'number':
				return (
					<input
						type="number"
						value={value}
						onChange={(e) => handleInputChange(field.key, parseFloat(e.target.value))}
						className={baseClasses}
						required={field.required}
						placeholder={`Entrez ${field.label.toLowerCase()}`}
					/>
				);

			case 'date':
				return (
					<input
						type="date"
						value={value}
						onChange={(e) => handleInputChange(field.key, e.target.value)}
						className={baseClasses}
						required={field.required}
					/>
				);

			case 'boolean':
				return (
					<label className="flex items-center space-x-3 cursor-pointer">
						<input
							type="checkbox"
							checked={!!value}
							onChange={(e) => handleInputChange(field.key, e.target.checked)}
							className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
							required={field.required}
						/>
						<span className="text-sm text-gray-700">Oui</span>
					</label>
				);

			case 'textarea':
				return (
					<textarea
						value={value}
						onChange={(e) => handleInputChange(field.key, e.target.value)}
						className={`${baseClasses} min-h-[100px] resize-y`}
						required={field.required}
						placeholder={`Entrez ${field.label.toLowerCase()}`}
					/>
				);

			default:
				return null;
		}
	};

	if (loadingConfig) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
			</div>
		);
	}

	if (!config) {
		return (
			<div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
				Configuration non disponible
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
			<h2 className="text-2xl font-bold text-gray-800 mb-6">
				{config.customStepName || 'Étape Personnalisée'}
			</h2>

			{errors.length > 0 && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
					<ul className="list-disc list-inside text-red-700 text-sm">
						{errors.map((error, index) => (
							<li key={index}>{error}</li>
						))}
					</ul>
				</div>
			)}

			<form onSubmit={handleSubmit} className="space-y-6">
				{config.customFields.length === 0 ? (
					<div className="text-center py-8 text-gray-500">
						Aucun champ personnalisé configuré.
						<br />
						Cliquez sur "Valider" pour passer à l'étape suivante.
					</div>
				) : (
					config.customFields.map((field) => (
						<div key={field.key} className="space-y-2">
							<label className="block text-sm font-medium text-gray-700">
								{field.label}
								{field.required && (
									<span className="text-red-500 ml-1">*</span>
								)}
							</label>
							{renderField(field)}
						</div>
					))
				)}

				<div className="flex items-center justify-end space-x-4 pt-4 border-t">
					{onCancel && (
						<button
							type="button"
							onClick={onCancel}
							className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
							disabled={loading}
						>
							Annuler
						</button>
					)}
					<button
						type="submit"
						disabled={loading}
						className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
					>
						{loading ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								<span>Validation...</span>
							</>
						) : (
							<span>Valider l'étape</span>
						)}
					</button>
				</div>
			</form>

			<div className="mt-6 pt-6 border-t">
				<div className="text-xs text-gray-500 text-center">
					Workflow : {config.nbSteps} étape{config.nbSteps > 1 ? 's' : ''} configurée{config.nbSteps > 1 ? 's' : ''}
				</div>
			</div>
		</div>
	);
};

export default DynamicProductionForm;
